import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Clock, User, Copy, Phone, Eye, AlertCircle, Trash2 } from 'lucide-react';
import { MechanicLogsService } from '../../services/mechanicLogsService';

const MechanicLogs = () => {
  const { mechanicId, complaintId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  const logsService = MechanicLogsService.getInstance();
  
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [mechanicId, complaintId]);

  useEffect(() => {
    filterLogs();
  }, [logs, searchQuery, filterType]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      await logsService.initialize();
      
      let allLogs;
      if (mechanicId) {
        allLogs = logsService.getMechanicLogs(mechanicId);
      } else if (complaintId) {
        allLogs = logsService.getComplaintLogs(complaintId);
      } else {
        allLogs = logsService.getAllLogs();
      }

      setLogs(allLogs);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadLogs();
  };

  const filterLogs = () => {
    let filtered = [...logs];

    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.action === filterType);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.mechanicName?.toLowerCase().includes(query) ||
        log.details?.toLowerCase().includes(query) ||
        log.userName?.toLowerCase().includes(query)
      );
    }

    setFilteredLogs(filtered);
  };

  const getActionColor = (action) => {
    const a = action?.toLowerCase() || '';
    if (a === 'assign') return 'bg-blue-100 text-blue-700';
    if (a === 'copy_info') return 'bg-green-100 text-green-700';
    if (a === 'call') return 'bg-purple-100 text-purple-700';
    if (a === 'view_details') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clearAllLogs = async () => {
    if (window.confirm('Are you sure you want to clear all mechanic logs? This action cannot be undone.')) {
      await logsService.clearLogs();
      await loadLogs();
    }
  };

  const clearMechanicLogs = async () => {
    if (!mechanicId) return;
    const mechanicName = location.state?.mechanic?.mechanic_name || location.state?.mechanic?.name || 'this mechanic';
    if (window.confirm(`Are you sure you want to clear all logs for ${mechanicName}?`)) {
      await logsService.clearMechanicLogs(mechanicId);
      await loadLogs();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center space-x-4 mb-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mechanic Logs</h1>
          <p className="text-gray-600">
            {mechanicId ? `For Mechanic ID: ${mechanicId.slice(0, 8)}` : ''}
            {complaintId ? `For Complaint ID: ${complaintId.slice(0, 8)}` : ''}
            {!mechanicId && !complaintId ? 'All Logs' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Action</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Actions</option>
              <option value="assign">Assign</option>
              <option value="copy_info">Copy Info</option>
              <option value="call">Call</option>
              <option value="view_details">View Details</option>
              <option value="search">Search</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-2">
          {mechanicId && (
            <button
              onClick={clearMechanicLogs}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center space-x-2"
            >
              <Trash2 size={18} />
              <span>Clear Mechanic Logs</span>
            </button>
          )}
          <button
            onClick={clearAllLogs}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center space-x-2"
          >
            <Trash2 size={18} />
            <span>Clear All Logs</span>
          </button>
        </div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading logs...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 mb-2">No logs found</p>
          <p className="text-sm text-gray-400">
            Logs will appear here when you perform actions like copying mechanic info, calling mechanics, or assigning mechanics.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLogs.map((log, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActionColor(log.action)}`}>
                    {log.action === 'assign' && <User size={20} />}
                    {log.action === 'copy_info' && <Copy size={20} />}
                    {log.action === 'call' && <Phone size={20} />}
                    {log.action === 'view_details' && <Eye size={20} />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{logsService.getActionDisplayText(log.action) || 'Unknown Action'}</h3>
                    <p className="text-sm text-gray-600">{formatTimestamp(log.timestamp)}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${getActionColor(log.action)}`}>
                  {log.action?.replace(/_/g, ' ') || 'Unknown'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {log.mechanicName && (
                  <div>
                    <p className="text-gray-500">Mechanic Name</p>
                    <p className="font-medium text-gray-900">{log.mechanicName}</p>
                  </div>
                )}
                {log.mechanicId && (
                  <div>
                    <p className="text-gray-500">Mechanic ID</p>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900">{log.mechanicId?.slice(0, 8)}</p>
                      <button onClick={() => copyToClipboard(log.mechanicId)} className="p-1 hover:bg-gray-100 rounded">
                        <Copy size={14} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}
                {log.userName && (
                  <div>
                    <p className="text-gray-500">User</p>
                    <p className="font-medium text-gray-900">{log.userName}</p>
                  </div>
                )}
                {log.complaintId && (
                  <div>
                    <p className="text-gray-500">Complaint ID</p>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900">{log.complaintId?.slice(0, 8)}</p>
                      <button onClick={() => copyToClipboard(log.complaintId)} className="p-1 hover:bg-gray-100 rounded">
                        <Copy size={14} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}
                {log.details && (
                  <div className="md:col-span-2">
                    <p className="text-gray-500">Details</p>
                    <p className="font-medium text-gray-900">{log.details}</p>
                  </div>
                )}
                {log.phoneNumber && (
                  <div>
                    <p className="text-gray-500">Phone Number</p>
                    <div className="flex items-center space-x-2">
                      <a href={`tel:${log.phoneNumber}`} className="font-medium text-gray-900 hover:text-teal-600">
                        {log.phoneNumber}
                      </a>
                      <button onClick={() => copyToClipboard(log.phoneNumber)} className="p-1 hover:bg-gray-100 rounded">
                        <Copy size={14} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MechanicLogs;

